import type { FC, ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { OnCustomerChange, UpdateCurrentCustomer, SessionCustomer, ExtendedSessionCustomer } from '@cld/session-service';
import { extendCustomer } from './utils';

export const CustomerIdContext = createContext<CustomerIdContextType | undefined>(undefined);

export const CustomerIdContextProvider: FC<CustomerIdContextProviderProps> = ({ onCustomerChange, updateCurrentCustomer, currentCustomer, children }) => {
  const [customer, setCustomer] = useState<SessionCustomer | undefined>(currentCustomer);

  useEffect(() => {
    if (!currentCustomer) {
      onCustomerChange?.(setCustomer);
    }

    if (currentCustomer && onCustomerChange) {
      console.warn(
        '[CustomerIdContextProvider]: when passing an explicit `currentCustomer` prop, `onCustomerChange` prop is being ignored. You should not pass both.'
      );
    }
  }, [currentCustomer, onCustomerChange]);

  const memoizedContextValue = useMemo(
    () => ({
      currentCustomer: extendCustomer(customer),
      updateCurrentCustomer,
    }),
    [updateCurrentCustomer, customer]
  );

  return <CustomerIdContext.Provider value={memoizedContextValue}>{children}</CustomerIdContext.Provider>;
};

export const useCustomerIdContext = () => {
  const value = useContext(CustomerIdContext);

  if (!value) {
    throw new Error('You need to wrap your component with CustomerIdContextProvider');
  }

  return value;
};

export interface CustomerIdContextProviderProps {
  onCustomerChange?: OnCustomerChange;
  updateCurrentCustomer: UpdateCurrentCustomer;
  currentCustomer?: ExtendedSessionCustomer;
  children?: ReactNode;
}

export interface CustomerIdContextType {
  updateCurrentCustomer: UpdateCurrentCustomer;
  currentCustomer?: ExtendedSessionCustomer;
}
